/**
 * Claiming a pairing code — the one door a screen ever comes through.
 *
 * The person using this is standing somewhere between a laptop and a television,
 * reading six characters off the screen across the room. So the dialog says where
 * the code is in plain words before it asks for anything, tells them the code is
 * safe to read out loud (it is: the half that authorises the device never leaves
 * the device), and when something goes wrong it names which of the three real
 * failures happened rather than echoing "not found".
 *
 * It stays open after a successful claim. Stores are commissioned a TV at a time
 * with the ladder already up, and closing the dialog to reopen it for the next
 * stick would be four extra clicks per store for no reason.
 *
 * Everything that is not layout — folding a typed code onto the pairing alphabet,
 * turning an API failure into a sentence — lives in `lib/claim.ts`, where it is
 * tested without a DOM.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PAIRING_CODE_LENGTH, screenClaimSchema } from '@pbtv/shared';
import type { RotationDegrees, ScreenClaim } from '@pbtv/shared';
import { Modal } from '../Modal.js';
import { ErrorBlock, InlineEmpty, LoadingBlock } from '../Feedback.js';
import { rotationLabel } from '../preview/geometry.js';
import {
  PAIRING_TTL_MINUTES,
  claimScreen,
  describeClaimFailure,
  excludedCharacterList,
  isCompleteUserCode,
  normaliseUserCode,
} from '../../lib/claim.js';
import type { ClaimedScreen } from '../../lib/claim.js';
import { FLEET_QUERY_KEY } from '../../lib/screens.js';
import { STORES_QUERY_KEY, listStores } from '../../lib/stores.js';
import './claim.css';

const ROTATIONS: readonly RotationDegrees[] = [0, 90, 180, 270];

export interface ClaimScreenDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ClaimScreenDialog({ open, onClose }: ClaimScreenDialogProps): ReactElement {
  return (
    <Modal
      open={open}
      size="wide"
      title="Claim a TV"
      description="A TV joins the fleet by showing a pairing code and having somebody claim it here. This is the only way a screen is created."
      onClose={onClose}
    >
      {/* Mounted only while open, so every visit starts on a clean form rather
          than on the last one's code and error. */}
      {open ? <ClaimForm onClose={onClose} /> : null}
    </Modal>
  );
}

interface ClaimFormProps {
  onClose: () => void;
}

/** What was claimed, kept for the confirmation after the form is reset. */
interface Claimed {
  screen: ClaimedScreen | null;
  name: string;
  storeName: string;
}

function ClaimForm({ onClose }: ClaimFormProps): ReactElement {
  const queryClient = useQueryClient();
  const codeRef = useRef<HTMLInputElement>(null);

  const stores = useQuery({
    queryKey: STORES_QUERY_KEY,
    queryFn: ({ signal }) => listStores(signal),
  });

  const [userCode, setUserCode] = useState('');
  const [storeId, setStoreId] = useState('');
  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [rotation, setRotation] = useState<RotationDegrees>(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<Claimed | null>(null);

  const options = useMemo(() => stores.data ?? [], [stores.data]);

  // One store is the common case for a manager, and picking it for them removes
  // the only field on this form whose answer was never in doubt.
  useEffect(() => {
    const only = options.length === 1 ? options[0] : undefined;
    if (only !== undefined && storeId === '') setStoreId(only.storeId);
  }, [options, storeId]);

  const claim = useMutation({
    mutationFn: (input: ScreenClaim) => claimScreen(input),
    onSuccess: async (screen, input) => {
      setClaimed({
        screen,
        name: input.name,
        storeName: options.find((store) => store.storeId === input.storeId)?.name ?? input.storeId,
      });
      setUserCode('');
      setName('');
      setSize('');
      await queryClient.invalidateQueries({ queryKey: FLEET_QUERY_KEY });
    },
  });

  const failure = claim.error === null ? null : describeClaimFailure(claim.error);

  // A rejected code is retyped, not corrected, so the cursor goes back to it with
  // the old attempt selected — one keystroke away from being replaced. Keyed on
  // the error itself rather than on the description derived from it: the
  // description is a fresh object every render, and re-selecting the field on
  // every keystroke would make it impossible to type in.
  useEffect(() => {
    if (claim.error === null) return;
    if (describeClaimFailure(claim.error).field !== 'userCode') return;
    codeRef.current?.focus();
    codeRef.current?.select();
  }, [claim.error]);

  if (stores.isPending) return <LoadingBlock label="Loading stores…" />;

  if (stores.isError) {
    return (
      <ErrorBlock
        error={stores.error}
        action="load the stores"
        onRetry={() => void stores.refetch()}
      />
    );
  }

  if (options.length === 0) {
    return (
      <InlineEmpty
        title="No store to claim into"
        description="A screen belongs to a store, and your account has not been given one. An administrator needs to create a store, or add you to an existing one, before a TV can be claimed."
      />
    );
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const trimmedSize = size.trim();
    const parsed = screenClaimSchema.safeParse({
      userCode,
      storeId,
      name: name.trim(),
      sizeInches: trimmedSize === '' ? null : Number(trimmedSize),
      rotationDegrees: rotation,
    });

    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Check the values above.');
      return;
    }

    setValidationError(null);
    setClaimed(null);
    claim.mutate(parsed.data);
  };

  const ready = isCompleteUserCode(userCode) && storeId !== '' && name.trim() !== '';

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <section className="claim__where">
        <h3 className="claim__where-title">The code is on the television</h3>
        <ol className="claim__steps">
          <li>Turn the TV on and switch it to the stick’s HDMI input.</li>
          <li>Open the Pizza Bros app. It shows a code in the middle of the screen.</li>
          <li>Type that code here within {PAIRING_TTL_MINUTES} minutes of it appearing.</li>
        </ol>
        <p className="claim__reassurance">
          The code on screen is safe for customers to see. It cannot pair anything on its own —
          the secret that authorises the device never leaves the stick.
        </p>
      </section>

      <div className="form__grid">
        <label className="field field--span">
          <span className="field__label">Pairing code</span>
          <input
            ref={codeRef}
            className="input input--code"
            type="text"
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            maxLength={PAIRING_CODE_LENGTH}
            placeholder={'X'.repeat(PAIRING_CODE_LENGTH)}
            value={userCode}
            onChange={(event) => setUserCode(normaliseUserCode(event.currentTarget.value))}
          />
          <span className="field__hint">
            Case does not matter, and a code never contains {excludedCharacterList()} — those
            characters are left out of the alphabet so nothing is ambiguous across a counter.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Store</span>
          <select
            className="input"
            value={storeId}
            onChange={(event) => setStoreId(event.currentTarget.value)}
          >
            <option value="">Choose a store…</option>
            {options.map((store) => (
              <option key={store.storeId} value={store.storeId}>
                {store.name}
              </option>
            ))}
          </select>
          <span className="field__hint">
            Where this TV physically is. It decides the timezone and opening hours the screen runs
            on.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Screen name</span>
          <input
            className="input"
            type="text"
            maxLength={120}
            placeholder="Counter left"
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <span className="field__hint">
            What staff call this TV. It shows on the idle card, so make it the name someone would
            use over the phone.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Physical size (inches)</span>
          <input
            className="input"
            type="number"
            inputMode="decimal"
            min={1}
            max={200}
            step={0.5}
            placeholder="Optional"
            value={size}
            onChange={(event) => setSize(event.currentTarget.value)}
          />
          <span className="field__hint">
            The screen’s diagonal. Used for planning, never for playback — leave it blank if nobody
            has measured it.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Rotation</span>
          <select
            className="input"
            value={rotation}
            onChange={(event) => setRotation(Number(event.currentTarget.value) as RotationDegrees)}
          >
            {ROTATIONS.map((value) => (
              <option key={value} value={value}>
                {value}° — {rotationLabel(value)}
              </option>
            ))}
          </select>
          <span className="field__hint">
            Which way the TV is turned on the wall. Editable later from this page — you often
            cannot tell from the office, and changing it needs no re-pairing.
          </span>
        </label>
      </div>

      {validationError !== null ? (
        <p className="alert alert--danger" role="alert">
          {validationError}
        </p>
      ) : null}

      {failure !== null ? (
        <div className="claim__failure" role="alert">
          <p className="claim__failure-title">{failure.title}</p>
          <p className="claim__failure-text">{failure.message}</p>
        </div>
      ) : null}

      {claimed !== null ? (
        <div className="claim__done" role="status">
          <p className="claim__done-title">
            {claimed.name} is paired to {claimed.storeName}
          </p>
          <p className="claim__done-text">
            It is in the list behind this dialog now, and picks up its content on the next
            heartbeat. Expect it to sit on the idle card while the first downloads land.
          </p>
          {claimed.screen === null ? null : (
            <p className="claim__done-text mono">{claimed.screen.screenId}</p>
          )}
          <p className="claim__done-text">
            Pairing another TV? Its code goes straight in the field above — the store and rotation
            are still set.
          </p>
        </div>
      ) : null}

      <div className="form__actions">
        <button type="button" className="button button--ghost" onClick={onClose}>
          {claimed === null ? 'Cancel' : 'Done'}
        </button>
        <button
          type="submit"
          className="button button--primary"
          disabled={claim.isPending || !ready}
        >
          {claim.isPending ? 'Claiming…' : 'Claim this TV'}
        </button>
      </div>
    </form>
  );
}
